import { useCallback, useEffect, useRef, useState } from "react"

import { format } from "date-fns"

import {
  applyInstanceToRoute,
  cloneRouteDates,
  findRouteInstanceOn,
  instanceStopsToPlannedStops,
  routePlannedToPlannedRoute,
} from "@/lib/plannedRouteMapping"
import type { PlannedRoute, RouteInstance, RoutePlanned } from "@/types/collections"

import type { InstanceEditMode } from "./types"

/** The timeline's edit buffer, seeded with each route's instance for the date; never re-seeds over unsaved edits. */
export function usePlannedRoutesForDate({
  routesPlannedData,
  routeInstances,
  selectedDate,
  instanceEditMode,
}: {
  /** The query result itself rather than its rows: a refetch re-seeds when the result changes. */
  routesPlannedData: { data: RoutePlanned[] } | undefined
  routeInstances: RouteInstance[] | undefined
  selectedDate: Date
  instanceEditMode: InstanceEditMode
}) {
  const [routes, setRoutes] = useState<PlannedRoute[]>([])
  const [originalRoutes, setOriginalRoutes] = useState<PlannedRoute[]>([])
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const initialTemplateRoutesRef = useRef<PlannedRoute[]>([])
  /** Last route/date seeded from an instance, so a refetch doesn't overwrite in-memory edits. */
  const instanceStopsSeededForRef = useRef<{ routeId: string; dateStr: string } | null>(null)

  useEffect(() => {
    if (hasUnsavedChanges) {
      return
    }

    if (routesPlannedData?.data) {
      if (routesPlannedData.data.length > 0) {
        const convertedRoutes: PlannedRoute[] =
          routesPlannedData.data.map(routePlannedToPlannedRoute)
        initialTemplateRoutesRef.current = convertedRoutes.map(cloneRouteDates)
        const routesToSet = routeInstances?.length
          ? convertedRoutes.map((route) => {
              const ri = findRouteInstanceOn(routeInstances, route.id, selectedDate)
              return ri?.stops?.length ? applyInstanceToRoute(route, ri, selectedDate) : route
            })
          : convertedRoutes
        setRoutes(routesToSet)
        setOriginalRoutes(routesToSet.map(cloneRouteDates))
      } else {
        setRoutes([])
        setOriginalRoutes([])
        initialTemplateRoutesRef.current = []
      }
    }
  }, [routesPlannedData, hasUnsavedChanges, selectedDate, routeInstances])

  // In instance edit mode, edit the instance's stops, not the template's.
  useEffect(() => {
    if (!instanceEditMode) {
      instanceStopsSeededForRef.current = null
      return
    }
    const dateStr = format(selectedDate, "yyyy-MM-dd")
    if (
      instanceStopsSeededForRef.current?.routeId === instanceEditMode.routeId &&
      instanceStopsSeededForRef.current?.dateStr === dateStr
    ) {
      return
    }
    if (!routeInstances?.length) return
    const ri = findRouteInstanceOn(routeInstances, instanceEditMode.routeId, selectedDate)
    if (!ri?.stops?.length) return
    const route = routes.find((r) => r.id === instanceEditMode.routeId)
    if (!route) return
    const instanceStops = instanceStopsToPlannedStops(ri.stops, route.id)
    setRoutes((prev) =>
      prev.map((r) => (r.id === instanceEditMode.routeId ? { ...r, stops: instanceStops } : r)),
    )
    instanceStopsSeededForRef.current = { routeId: instanceEditMode.routeId, dateStr }
  }, [instanceEditMode, selectedDate, routeInstances, routes])

  // Seed with the date's instance (or the template), resetting originalRoutes so nothing reads as unsaved.
  useEffect(() => {
    if (hasUnsavedChanges) return
    const template = initialTemplateRoutesRef.current

    const nextRoutes = (prev: PlannedRoute[]) =>
      prev.map((route) => {
        const ri = findRouteInstanceOn(routeInstances, route.id, selectedDate)
        if (ri?.stops?.length) return applyInstanceToRoute(route, ri, selectedDate)
        const templateRoute = template?.find((r) => r.id === route.id)
        return templateRoute ? cloneRouteDates(templateRoute) : route
      })

    setRoutes((prev) => {
      const updated = nextRoutes(prev)
      setOriginalRoutes(updated.map(cloneRouteDates))
      return updated
    })
  }, [selectedDate, routeInstances, hasUnsavedChanges])

  /** The first edit snapshots from the updater's own `prev`, so the snapshot is never one edit late. */
  const editRoutes = useCallback(
    (update: (prevRoutes: PlannedRoute[]) => PlannedRoute[]) => {
      setRoutes((prevRoutes) => {
        const updated = update(prevRoutes)
        setOriginalRoutes((prevOriginal) => {
          if (prevOriginal.length === 0 && !hasUnsavedChanges) {
            return prevRoutes.map(cloneRouteDates)
          }
          return prevOriginal
        })
        return updated
      })
      setHasUnsavedChanges(true)
    },
    [hasUnsavedChanges],
  )

  const deleteStop = useCallback(
    (routeId: string, stopId: string) => {
      editRoutes((prevRoutes) =>
        prevRoutes.map((route) => {
          if (route.id === routeId) {
            const updatedStops = route.stops
              .filter((s) => s.id !== stopId)
              .map((stop, idx) => ({ ...stop, sequence: idx + 1 }))
            return {
              ...route,
              stops: updatedStops,
              totalStops: updatedStops.length,
              updatedAt: new Date(),
            }
          }
          return route
        }),
      )
    },
    [editRoutes],
  )

  /** Haulage delivery revenue or direct-injection price; a stop never carries both. */
  const updateStopMoney = useCallback(
    (
      routeId: string,
      stopId: string,
      field: "haulageDeliveryRevenueGbp" | "directInjectionCostGbp",
      value: number | undefined,
    ) => {
      editRoutes((prevRoutes) =>
        prevRoutes.map((route) => {
          if (route.id !== routeId) return route
          return {
            ...route,
            stops: route.stops.map((s) => (s.id === stopId ? { ...s, [field]: value } : s)),
            updatedAt: new Date(),
          }
        }),
      )
    },
    [editRoutes],
  )

  return {
    routes,
    setRoutes,
    originalRoutes,
    setOriginalRoutes,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    initialTemplateRoutesRef,
    editRoutes,
    deleteStop,
    updateStopMoney,
  }
}
